import dayjs from 'dayjs';

import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import ChartHeader from 'components/charts/ChartHeader';
import DataCards from 'components/charts/DataCards';
import AutoComplete from 'components/field/AutoComplete';
import DateFilter from 'components/field/DateFilter';
import Typography from 'components/global/Typography';
import WidgetPageContainer from 'components/global/WidgetPageContainer';
import WidgetWrapper from 'components/global/WidgetWrapper';

import useAccessControl from 'hooks/useAccessControl';
import useFetchAndParseOnView from 'hooks/useFetchAndParseOnView';

import { AUTOCOMPLETE, REPORT } from 'services/consts';

const OTOPackTab = () => {
	const isAuthorized = useAccessControl();
	const user = useSelector((state) => state.user);
	const defaultWarehouseID = user?.generalSettings?.defaultWarehouse;

	const [warehouseID, setWarehouseID] = useState(defaultWarehouseID);

	const warehouseAutocompleteColumns = [{ accessor: 'Name', width: 200 }];

	const cardsConfigWithRole = useCallback(
		(cardsConfig) => cardsConfig.filter((card) => !card.role || isAuthorized(card.role)),
		[isAuthorized]
	);

	return (
		<WidgetPageContainer>
			<Typography variant="p3" fontWeight="500" className="mb-3" noWrap style={{ gridColumn: 'span 6' }}>
				selectWarehouseToSeeStatistics
			</Typography>
			<div style={{ gridColumn: 'span 3' }}>
				<AutoComplete
					autoCompleteName={AUTOCOMPLETE.WAREHOUSES}
					columns={warehouseAutocompleteColumns}
					value={warehouseID}
					setValue={(value) => setWarehouseID(value?.ID ?? null)}
					selectionLabelAccessor={['Name']}
					placeholder="warehouse"
				/>
			</div>
			{isAuthorized('IMSModule') && (
				<WidgetWrapper widgetId={'inboundStatistics'}>
					<InboundStatistics warehouseID={warehouseID} cardsConfigWithRole={cardsConfigWithRole} />
				</WidgetWrapper>
			)}
			{isAuthorized(['WMSModule', 'WMSAdvanceModule']) && (
				<WidgetWrapper widgetId={'pickStatistics'}>
					<PickStatistics warehouseID={warehouseID} cardsConfigWithRole={cardsConfigWithRole} />
				</WidgetWrapper>
			)}
			{isAuthorized(['WMSModule', 'WMSAdvanceModule']) && (
				<WidgetWrapper widgetId={'packStatistics'}>
					<PackStatistics warehouseID={warehouseID} cardsConfigWithRole={cardsConfigWithRole} />
				</WidgetWrapper>
			)}

			<WidgetWrapper widgetId={'returnStatistics'}>
				<ReturnStatistics warehouseID={warehouseID} cardsConfigWithRole={cardsConfigWithRole} />
			</WidgetWrapper>
		</WidgetPageContainer>
	);
};

const InboundStatistics = ({ warehouseID, cardsConfigWithRole }) => {
	const [filter, setFilter] = useState({
		start: dayjs().startOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	const body = useMemo(() => ({ from: filter.start, to: filter.end, warehouseID }), [filter, warehouseID]);

	const cardsConfig = [
		{
			accessor: 'totalItemsInInboundAreas',
			label: 'totalItemsInInboundAreas',
			customIcon: 'fa-regular-box-circle-arrow-down',
			backgroundColor: 'var(--fa-regular-box-circle-arrow-down-bgColor)',
			color: 'var(--fa-regular-box-circle-arrow-down-color)',
			role: 'WMS_BinTracking'
		},
		{
			accessor: 'totalNumOfCompletedInboundOrders',
			label: 'totalNumOfCompletedInboundOrders',
			customIcon: 'fa-regular-box-circle-check',
			backgroundColor: 'var(--fa-regular-box-circle-check-bgColor)',
			color: 'var(--fa-regular-box-circle-check-color)'
		},
		{
			accessor: 'totalNumOfPendingInboundOrders',
			label: 'totalNumOfPendingInboundOrders',
			customIcon: 'fa-regular-box-clock',
			backgroundColor: 'var(--fa-regular-box-clock-bgColor)',
			color: 'var(--fa-regular-box-clock-color)'
		},
		{
			accessor: 'totalQTYReceived',
			label: 'totalQTYReceived',
			customIcon: 'fa fas fa-check-double',
			backgroundColor: 'var(--fa-check-double-bgColor)',
			color: 'var(--fa-check-double-color)'
		},
		{
			accessor: 'totalSKUsReceived',
			label: 'totalSKUsReceived',
			customIcon: 'fa fas fa-layer-group',
			backgroundColor: 'var(--fa-layer-group-bgColor)',
			color: 'var(--fa-layer-group-color)'
		}
	];

	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.OTO_PACK.GET_INBOUND_STATISTIC,
		body: body ?? {}
	});

	return (
		<div ref={ref}>
			<ChartHeader
				title={'inboundStatistics'}
				filter={<DateFilter defaultDay={'today'} setValue={setFilter} />}
			/>
			<DataCards data={data} loading={loading} cardsConfig={cardsConfigWithRole(cardsConfig)} />
		</div>
	);
};

const PickStatistics = ({ warehouseID, cardsConfigWithRole }) => {
	const [filter, setFilter] = useState({
		start: dayjs().startOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	const body = useMemo(() => ({ from: filter.start, to: filter.end, warehouseID }), [filter, warehouseID]);

	const cardsConfig = [
		{
			accessor: 'avgCompletionTimeForWaves',
			label: 'avgCompletionTimeForWaves',
			customIcon: 'fas fa-stopwatch',
			tooltip: 'dashboard.avgCompletionTimeForWaves.description',
			backgroundColor: 'var(--fa-stopwatch-bgColor)',
			color: 'var(--fa-stopwatch-color)',
			role: 'WMS_BinTracking'
		},
		{
			accessor: 'avgWaitingTimeToStartPicking',
			label: 'avgWaitingTimeToStartPicking',
			customIcon: 'fas fa-hourglass-half',
			tooltip: 'dashboard.avgWaitingTimeToStartPicking.description',
			backgroundColor: 'var(--fa-hourglass-half-bgColor)',
			color: 'var(--fa-hourglass-half-color)',
			role: 'WMS_BinTracking'
		},
		{
			accessor: 'avgWaveSize',
			label: 'avgWaveSize',
			customIcon: 'fas fa-balance-scale',
			tooltip: 'dashboard.avgWaveSize.description',
			backgroundColor: 'var(--fa-balance-scale-bgColor)',
			color: 'var(--fa-balance-scale-color)',
			role: 'WMS_BinTracking'
		},
		{
			accessor: 'maxCompletionTime',
			label: 'maxCompletionTime',
			customIcon: 'fas fa-long-arrow-alt-up',
			tooltip: 'dashboard.maxCompletionTime.description',
			backgroundColor: 'var(--fa-long-arrow-alt-up-bgColor)',
			color: 'var(--fa-long-arrow-alt-up-color)',
			role: 'WMS_BinTracking'
		},
		{
			accessor: 'minCompletionTime',
			label: 'minCompletionTime',
			customIcon: 'fas fa-long-arrow-alt-down',
			tooltip: 'dashboard.minCompletionTime.description',
			backgroundColor: 'var(--fa-long-arrow-alt-down-bgColor)',
			color: 'var(--fa-long-arrow-alt-down-color)',
			role: 'WMS_BinTracking'
		},
		{
			accessor: 'totalItemQuantityPendingPicking',
			label: 'totalItemQuantityPendingPicking',
			customIcon: 'fas fa-hourglass-start',
			tooltip: 'dashboard.totalItemQuantityPendingPicking.description',
			backgroundColor: 'var(--fa-hourglass-start-bgColor)',
			color: 'var(--fa-hourglass-start-color)'
		},
		{
			accessor: 'totalItemQuantityPickedSQL',
			label: 'totalItemQuantityPickedSQL',
			customIcon: 'fas fa-boxes',
			tooltip: 'dashboard.totalItemQuantityPickedSQL.description',
			backgroundColor: 'var(--fa-boxes-bgColor)',
			color: 'var(--fa-boxes-color)'
		},
		{
			accessor: 'totalNumberOfOrdersPicked',
			label: 'totalNumberOfOrdersPicked',
			customIcon: 'fas fa-shopping-bag',
			tooltip: 'dashboard.totalNumberOfOrdersPicked.description',
			backgroundColor: 'var(--fa-shopping-bag-bgColor)',
			color: 'var(--fa-shopping-bag-color)'
		},
		{
			accessor: 'totalPendingWaves',
			label: 'totalPendingWaves',
			customIcon: 'fas fa-hourglass-half',
			tooltip: 'dashboard.totalPendingWaves.description',
			backgroundColor: 'var(--fa-hourglass-half-bgColor)',
			color: 'var(--fa-hourglass-half-color)',
			role: 'WMS_BinTracking'
		},
		{
			accessor: 'totalWavesCompleted',
			label: 'totalWavesCompleted',
			customIcon: 'fas fa-check-circle',
			tooltip: 'dashboard.totalWavesCompleted.description',
			backgroundColor: 'var(--fa-check-circle-bgColor)',
			color: 'var(--fa-check-circle-color)',
			role: 'WMS_BinTracking'
		}
	];

	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.OTO_PACK.GET_PICK_STATISTIC,
		body: body ?? {}
	});

	return (
		<div ref={ref}>
			<ChartHeader title={'pickStatistics'} filter={<DateFilter defaultDay={'today'} setValue={setFilter} />} />
			<DataCards data={data} loading={loading} cardsConfig={cardsConfigWithRole(cardsConfig)} />
		</div>
	);
};

const PackStatistics = ({ warehouseID, cardsConfigWithRole }) => {
	const [filter, setFilter] = useState({
		start: dayjs().startOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	const body = useMemo(() => ({ from: filter.start, to: filter.end, warehouseID }), [filter, warehouseID]);

	const cardsConfig = [
		{
			accessor: 'totalItemsInPackingAreas',
			label: 'totalItemsInPackingAreas',
			customIcon: 'fa fas fa-cubes',
			tooltip: 'dashboard.totalItemsInPackingAreas.description',
			backgroundColor: 'var(--fa-cubes-bgColor)',
			color: 'var(--fa-cubes-color)',
			role: 'WMS_BinTracking'
		},
		{
			accessor: 'totalPackedOrder',
			label: 'totalPackedOrder',
			customIcon: 'fa fas fa-check-square-o',
			tooltip: 'dashboard.totalPackedOrder.description',
			backgroundColor: 'var(--fa-check-square-o-bgColor)',
			color: 'var(--fa-check-square-o-color)'
		},
		{
			accessor: 'totalPackedOrderQuantity',
			label: 'totalPackedOrderQuantity',
			customIcon: 'fa fas fa-archive',
			tooltip: 'dashboard.totalPackedOrderQuantity.description',
			backgroundColor: 'var(--fa-archive-bgColor)',
			color: 'var(--fa-archive-color)'
		},
		{
			accessor: 'totalPendingPackedOrder',
			label: 'totalPendingPackedOrder',
			customIcon: 'fa fas fa-exclamation-triangle',
			tooltip: 'dashboard.totalPendingPackedOrder.description',
			backgroundColor: 'var(--fa-exclamation-triangle-bgColor)',
			color: 'var(--fa-exclamation-triangle-color)'
		},
		{
			accessor: 'totalPendingPackedOrderQuantity',
			label: 'totalPendingPackedOrderQuantity',
			customIcon: 'fa fas fa-hourglass-start',
			tooltip: 'dashboard.totalPendingPackedOrderQuantity.description',
			backgroundColor: 'var(--fa-hourglass-start-bgColor)',
			color: 'var(--fa-hourglass-start-color)'
		}
	];

	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.OTO_PACK.GET_PACK_STATISTIC,
		body: body ?? {}
	});

	return (
		<div ref={ref}>
			<ChartHeader title={'packStatistics'} filter={<DateFilter defaultDay={'today'} setValue={setFilter} />} />
			<DataCards data={data} loading={loading} cardsConfig={cardsConfigWithRole(cardsConfig)} />
		</div>
	);
};

const ReturnStatistics = ({ warehouseID, cardsConfigWithRole }) => {
	const [filter, setFilter] = useState({
		start: dayjs().startOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	const body = useMemo(() => ({ from: filter.start, to: filter.end, warehouseID }), [filter, warehouseID]);

	const cardsConfig = [
		{
			accessor: 'avgTimeToConfirmReturn',
			label: 'avgTimeToConfirmReturn',
			customIcon: 'fa fas fa-clock-o',
			tooltip: 'dashboard.avgTimeToConfirmReturn.description',
			backgroundColor: 'var(--fa-clock-o-bgColor)',
			color: 'var(--fa-clock-o-color)'
		},
		{
			accessor: 'totalItemsInReturnArea',
			label: 'totalItemsInReturnArea',
			customIcon: 'fa fas fa-truck',
			tooltip: 'dashboard.totalItemsInReturnArea.description',
			backgroundColor: 'var(--fa-truck-bgColor)',
			color: 'var(--fa-truck-color)',
			role: 'WMS_BinTracking'
		},
		{
			accessor: 'totalNumberOfConfirmedReturnOrders',
			label: 'totalNumberOfConfirmedReturnOrders',
			customIcon: 'fa fas fa-check-square-o',
			tooltip: 'dashboard.totalNumberOfConfirmedReturnOrders.description',
			backgroundColor: 'var(--fa-check-square-o-bgColor)',
			color: 'var(--fa-check-square-o-color)'
		},
		{
			accessor: 'totalNumberOfPendingReturnOrders',
			label: 'totalNumberOfPendingReturnOrders',
			customIcon: 'fa fas fa-undo',
			tooltip: 'dashboard.totalNumberOfPendingReturnOrders.description',
			backgroundColor: 'var(--fa-undo-bgColor)',
			color: 'var(--fa-undo-color)'
		}
	];

	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.OTO_PACK.GET_RETURN_STATISTIC,
		body: body ?? {}
	});

	return (
		<div ref={ref}>
			<ChartHeader title={'returnStatistics'} filter={<DateFilter defaultDay={'today'} setValue={setFilter} />} />
			<DataCards data={data} loading={loading} cardsConfig={cardsConfigWithRole(cardsConfig)} />
		</div>
	);
};

export default OTOPackTab;
